// GameAssets.js
import React, { useRef, useEffect, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { GAME_CONFIG } from './GameConfig';
import { Html } from '@react-three/drei';

// --- PERFORMANCE OPTIMIZATION: SHARED VECTORS ---
// useFrame içinde sürekli 'new Vector3' oluşturmak yerine bunları tekrar kullanıyoruz.
const _tempVec = new THREE.Vector3();
const _tempDir = new THREE.Vector3();
const _targetPos = new THREE.Vector3();

// 1. Click Marker
export function ClickMarker({ position, visible }) {
  const mesh = useRef();
  useFrame((state, delta) => {
    if (mesh.current && visible) {
      mesh.current.rotation.z -= delta * 8;
      mesh.current.scale.lerp(_tempVec.set(0, 0, 0), delta * 5);
    }
  });
  if (!visible) return null;
  return (
    <mesh ref={mesh} position={[position.x, 0.2, position.z]} rotation={[-Math.PI/2, 0, 0]}>
      <ringGeometry args={[0.5, 0.7, 32]} />
      <meshBasicMaterial color="#00ff00" transparent opacity={0.8} />
    </mesh>
  );
}

// 2. Skill Marker
export const SkillMarker = ({ position, visible, color, type }) => {
    const group = useRef(); 
    const mesh = useRef();

    useFrame(() => {
        if (!visible) return;
        if (group.current && position) group.current.position.copy(position);
        if (mesh.current && type === 'pad') mesh.current.rotation.z += 0.05;
    });

    if (!visible) return null;

    return (
        <group ref={group}>
            {type === 'shadow' ? (
                 <mesh ref={mesh} position={[0, 1, 0]}>
                    <capsuleGeometry args={[GAME_CONFIG.PLAYER_RADIUS * 0.9, 1.7, 4, 8]} />
                    <meshStandardMaterial color="#000" emissive="#111" transparent opacity={0.7} />
                    <mesh position={[0, 0.5, 0.4]}>
                        <boxGeometry args={[0.6, 0.2, 0.3]} />
                        <meshStandardMaterial color="#ff0000" emissive="#ff0000" />
                    </mesh>
                 </mesh>
            ) : (
                <mesh ref={mesh} rotation={[-Math.PI/2, 0, 0]} position={[0, 0.1, 0]}>
                    <ringGeometry args={[1.0, 1.2, 32]} />
                    <meshBasicMaterial color={color} transparent opacity={0.8} side={THREE.DoubleSide} />
                </mesh>
            )}
        </group>
    );
};

// 3. Yasuo Wind Wall
export const WindWall = ({ position, rotation, visible }) => {
    const group = useRef();
    const mesh = useRef();
    
    const champConfig = GAME_CONFIG.CHAMPIONS.yasuo; // Yasuo configini çek

    useFrame((state) => {
        if (!visible) return;
        if (group.current && position) {
            group.current.position.copy(position);
            group.current.rotation.y = -rotation; 
        }
        if (mesh.current) {
            mesh.current.material.opacity = 0.5 + Math.sin(state.clock.elapsedTime * 8) * 0.2;
        }
    });

    if (!visible) return null;
    
    return (
        <group ref={group}>
            <mesh ref={mesh} position={[0, 1.5, 0]}>
                <boxGeometry args={[champConfig.WALL_WIDTH, 3, 0.2]} />
                <meshStandardMaterial color="#00ccff" transparent opacity={0.5} side={THREE.DoubleSide} />
            </mesh>
            <mesh position={[0, 0.1, 0]} rotation={[-Math.PI/2, 0, 0]}>
                <planeGeometry args={[champConfig.WALL_WIDTH, 0.8]} />
                <meshBasicMaterial color="#fff" transparent opacity={0.2} />
            </mesh>
        </group>
    );
};

// 4. Flash Ghost
export const FlashGhost = ({ position, visible, onComplete }) => {
    const mesh = useRef();
    useFrame((state, delta) => {
        if (!visible || !mesh.current) return;
        mesh.current.material.opacity -= delta * 2; 
        if (mesh.current.material.opacity <= 0) onComplete();
    });
    if (!visible) return null;
    return (
        <mesh ref={mesh} position={position} rotation={[0,0,0]}>
            <capsuleGeometry args={[GAME_CONFIG.PLAYER_RADIUS, 1.8, 4, 8]} />
            <meshBasicMaterial color="#ffff00" transparent opacity={0.6} />
        </mesh>
    );
};

// 5. Projectiles (OPTIMIZED)
export const Projectile = ({ data, playerPosRef, activeWall, onHit, onDespawn, isGameOver, isPaused, isZhonya }) => {
    const group = useRef(); 
    const hasHitRef = useRef(false);
    const isJhin = data.isJhin || false;
    const isCrit = data.isCrit || false;

    // Initial LookAt
    useEffect(() => {
        if (group.current) {
            _tempVec.copy(group.current.position).add(data.velocity);
            group.current.lookAt(_tempVec);
        }
    }, [data.velocity]);

    useFrame((state, delta) => {
        if (isGameOver || isPaused || !group.current || hasHitRef.current) return;

        // GC Optimization: Clone yerine copy kullanıyoruz
        _tempDir.copy(data.velocity).multiplyScalar(delta);
        group.current.position.add(_tempDir);
        const currentPos = group.current.position;

        // YASUO DUVAR ÇARPIŞMASI
        if (activeWall && activeWall.visible) {
             const dx = currentPos.x - activeWall.pos.x;
             const dz = currentPos.z - activeWall.pos.z;
             // Matematiksel hesaplama
             const localX = dx * Math.cos(activeWall.rot) - dz * Math.sin(activeWall.rot);
             const localZ = dx * Math.sin(activeWall.rot) + dz * Math.cos(activeWall.rot);
             
             // Yasuo duvar genişliğini config'den al
             const wallWidth = GAME_CONFIG.CHAMPIONS.yasuo.WALL_WIDTH;

             if (Math.abs(localZ) < 1.0 && Math.abs(localX) < (wallWidth / 2 + 0.5)) {
                 onDespawn(data.id); 
                 return;
             }
        }
        
        // OYUNCUYA ÇARPMA
        if (playerPosRef.current && !isZhonya) {
            const dist = currentPos.distanceTo(playerPosRef.current);
            const radius = isJhin ? 0.6 : (GAME_CONFIG.PROJECTILE_RADIUS);
            if (dist < (GAME_CONFIG.PLAYER_RADIUS + radius)) {
                hasHitRef.current = true;
                onHit(isCrit ? 2 : 1); 
                onDespawn(data.id);
                return;
            }
        }
        
        // MENZİL KONTROLÜ
        if (isJhin) {
            const traveled = currentPos.distanceTo(data.startPos);
            if (traveled > GAME_CONFIG.JHIN_ULT_RANGE) onDespawn(data.id);
        } else {
            // Basit uzunluk kontrolü (kareköksüz işlem daha hızlıdır ama burada length() yeterli)
            if (currentPos.lengthSq() > (GAME_CONFIG.MAP_SIZE + 20) ** 2) onDespawn(data.id);
        }
    });

    // Render Jhin Bullet or Normal Projectile
    if (isJhin) {
        return (
            <group ref={group} position={data.startPos}>
                <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, -2]}> 
                     <cylinderGeometry args={[isCrit ? 0.2 : 0.1, isCrit ? 0.2 : 0.1, 8, 8]} />
                     <meshBasicMaterial color={isCrit ? "#ff0000" : "#d4af37"} />
                </mesh>
                <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, -2]}>
                     <cylinderGeometry args={[isCrit ? 0.4 : 0.2, isCrit ? 0.4 : 0.2, 8.2, 8]} />
                     <meshBasicMaterial color={isCrit ? "#ff0044" : "#ffcc00"} transparent opacity={0.6} blending={THREE.AdditiveBlending} />
                </mesh>
                <mesh position={[0, 0, 2]}>
                    <sphereGeometry args={[isCrit ? 0.5 : 0.3, 16, 16]} />
                    <meshBasicMaterial color="#ffffff" />
                </mesh>
            </group>
        );
    }

    return (
        <group ref={group} position={data.startPos}>
            <mesh rotation={[-Math.PI / 2, 0, 0]}>
                <coneGeometry args={[0.2, 1.2, 8]} />
                <meshStandardMaterial color="#ffeebb" emissive="#ffaa00" emissiveIntensity={3} toneMapped={false} />
            </mesh>
        </group>
    );
};

// 6. XERATH W
export const XerathW = ({ data, playerPosRef, onHit, onComplete, isPaused, isGameOver, isZhonya }) => {
    const outerRingRef = useRef();   
    const innerEyeRef = useRef();    
    const beamRef = useRef();        
    const shockwaveRef = useRef();   
    const startTime = useRef(Date.now());
    const damageDealt = useRef(false);

    useFrame(() => {
        if (isPaused || isGameOver) return;
        const now = Date.now();
        const elapsed = now - startTime.current;
        
        if (elapsed < GAME_CONFIG.XERATH_DELAY) {
            const progress = elapsed / GAME_CONFIG.XERATH_DELAY;
            if (outerRingRef.current) { 
                outerRingRef.current.rotation.z += 0.02; 
                outerRingRef.current.material.opacity = 0.5 + (progress * 0.3); 
            }
            if (innerEyeRef.current) { 
                const scale = THREE.MathUtils.lerp(0, GAME_CONFIG.XERATH_RADIUS, progress); 
                innerEyeRef.current.scale.set(scale, scale, 1); 
                innerEyeRef.current.material.emissiveIntensity = 1 + progress * 2; 
            }
        } else {
            if (!damageDealt.current) {
                damageDealt.current = true; 
                if (innerEyeRef.current) innerEyeRef.current.visible = false;
                if (outerRingRef.current) outerRingRef.current.visible = false;
                if (beamRef.current) beamRef.current.visible = true;
                if (shockwaveRef.current) shockwaveRef.current.visible = true;
                
                if (playerPosRef.current && !isZhonya) {
                    _tempVec.set(data.position.x, 0, data.position.z);
                    _targetPos.set(playerPosRef.current.x, 0, playerPosRef.current.z);
                    if (_tempVec.distanceTo(_targetPos) < (GAME_CONFIG.XERATH_RADIUS + GAME_CONFIG.PLAYER_RADIUS * 0.5)) { 
                        onHit(1); 
                    }
                }
            }
            const explosionTime = elapsed - GAME_CONFIG.XERATH_DELAY;
            const fadeOut = 1 - (explosionTime / GAME_CONFIG.XERATH_DAMAGE_DURATION);
            
            if (beamRef.current) { 
                beamRef.current.material.opacity = fadeOut * 0.8; 
                const s = 1 - (explosionTime * 0.002);
                beamRef.current.scale.set(s, 1, s); 
            }
            if (shockwaveRef.current) { 
                shockwaveRef.current.material.opacity = fadeOut; 
                const popScale = Math.min(1.0, 0.8 + (explosionTime * 0.005)); 
                shockwaveRef.current.scale.set(popScale, popScale, 1); 
            }
        }
        if (elapsed > (GAME_CONFIG.XERATH_DELAY + GAME_CONFIG.XERATH_DAMAGE_DURATION)) { onComplete(data.id); }
    });
    
    return (
        <group position={data.position}>
            <mesh ref={outerRingRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
                <ringGeometry args={[GAME_CONFIG.XERATH_RADIUS - 0.2, GAME_CONFIG.XERATH_RADIUS, 64]} />
                <meshBasicMaterial color="#0066ff" transparent opacity={0.5} side={THREE.DoubleSide} />
            </mesh>
            <mesh ref={innerEyeRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0]}>
                <circleGeometry args={[1, 64]} />
                <meshStandardMaterial color="#00ffff" emissive="#0088ff" emissiveIntensity={1} transparent opacity={0.6} />
            </mesh>
            <mesh ref={beamRef} position={[0, 10, 0]} visible={false}>
                <cylinderGeometry args={[0.3, 0.3, 20, 16]} />
                <meshStandardMaterial color="#ffffff" emissive="#00ccff" emissiveIntensity={5} transparent opacity={0.9} toneMapped={false} depthWrite={false} blending={THREE.AdditiveBlending} />
            </mesh>
            <mesh ref={shockwaveRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.1, 0]} visible={false}>
                <ringGeometry args={[0, GAME_CONFIG.XERATH_RADIUS, 64]} /> 
                <meshBasicMaterial color="#0088ff" transparent opacity={0.8} side={THREE.DoubleSide} blending={THREE.AdditiveBlending} />
            </mesh>
        </group>
    );
};

// 7. LUX ULT
export const LuxUlt = ({ data, playerPosRef, onHit, onComplete, isPaused, isGameOver, isZhonya }) => {
    const beamRef = useRef(); const coreRef = useRef(); const glowRef = useRef(); const warningRef = useRef();
    const startTime = useRef(Date.now()); const damageDealt = useRef(false);

    useFrame(() => {
        if (isPaused || isGameOver) return;
        const now = Date.now(); const elapsed = now - startTime.current;
        
        if (elapsed < GAME_CONFIG.LUX_DELAY) {
            if (warningRef.current) { 
                warningRef.current.visible = true; 
                warningRef.current.material.opacity = 0.5 + Math.sin(elapsed * 0.01) * 0.3; 
            }
            if (coreRef.current) coreRef.current.visible = false; 
            if (glowRef.current) glowRef.current.visible = false;
        } else {
            if (!damageDealt.current) {
                damageDealt.current = true;
                if (warningRef.current) warningRef.current.visible = false; 
                if (coreRef.current) coreRef.current.visible = true; 
                if (glowRef.current) glowRef.current.visible = true;
                
                if (playerPosRef.current && !isZhonya) {
                    const angle = data.rotation; 
                    const dx = playerPosRef.current.x - data.position.x; 
                    const dz = playerPosRef.current.z - data.position.z;
                    // Rotasyonlu çarpışma kontrolü
                    const rotatedX = dx * Math.cos(-angle) - dz * Math.sin(-angle);
                    if (Math.abs(rotatedX) < (GAME_CONFIG.LUX_WIDTH / 2 + GAME_CONFIG.PLAYER_RADIUS)) onHit(1);
                }
            }
            const fireTime = elapsed - GAME_CONFIG.LUX_DELAY; 
            const fadeOut = Math.max(0, 1 - (fireTime / GAME_CONFIG.LUX_DURATION));
            
            if (coreRef.current && glowRef.current) { 
                const widthScale = 1 + Math.sin(fireTime * 0.01) * 0.1; 
                coreRef.current.scale.set(widthScale, 1, 1); 
                glowRef.current.scale.set(widthScale * 1.5, 1, 1); 
                coreRef.current.material.opacity = fadeOut; 
                glowRef.current.material.opacity = fadeOut * 0.6; 
            }
        }
        if (elapsed > (GAME_CONFIG.LUX_DELAY + GAME_CONFIG.LUX_DURATION)) { onComplete(data.id); }
    });

    return (
        <group position={data.position} rotation={[0, -data.rotation, 0]}>
             <mesh ref={warningRef} position={[0, 0.5, 0]}> <boxGeometry args={[0.2, 0.2, GAME_CONFIG.MAP_SIZE * 3]} /> <meshBasicMaterial color="#ff0000" transparent opacity={0.5} /> </mesh>
             <mesh ref={coreRef} position={[0, 0.5, 0]} visible={false}> <boxGeometry args={[GAME_CONFIG.LUX_WIDTH * 0.7, 1, GAME_CONFIG.MAP_SIZE * 3]} /> <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={4} toneMapped={false} transparent /> </mesh>
             <mesh ref={glowRef} position={[0, 0.5, 0]} visible={false}> <boxGeometry args={[GAME_CONFIG.LUX_WIDTH, 1.2, GAME_CONFIG.MAP_SIZE * 3]} /> <meshStandardMaterial color="#ffaa00" emissive="#ff00ff" emissiveIntensity={2} transparent opacity={0.5} toneMapped={false} blending={THREE.AdditiveBlending} /> </mesh>
        </group>
    );
}

// 8. Mastery Emote
export const MasteryEmote = ({ visible, positionRef }) => {
    const group = useRef();
    useFrame(() => {
        if (!visible || !group.current || !positionRef.current) return;
        group.current.position.set(positionRef.current.x, 3.5, positionRef.current.z);
    });
    if (!visible) return null;
    return (
        <group ref={group}>
            <Html center distanceFactor={12} zIndexRange={[1000, 0]} style={{ pointerEvents: 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '0px', height: '0px' }}>
                    <img src="/m7.png" alt="M7" style={{ width: '180px', height: 'auto', animation: 'popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)', filter: 'drop-shadow(0 0 15px rgba(0, 150, 255, 0.6))', transformOrigin: 'center center', imageRendering: 'high-quality' }} />
                </div>
            </Html>
        </group>
    );
};

// 9. Item Orb
export const ItemOrb = ({ data }) => {
    const mesh = useRef();
    const isBanshee = data.type === 'banshee';
    const isGhost = data.type === 'ghost';
    const color = isBanshee ? "#a020f0" : isGhost ? "#00ffff" : "#00ff00";
    const floatSpeed = isBanshee ? 3 : isGhost ? 5 : 2;

    useFrame((state) => {
        if (!mesh.current) return;
        const time = state.clock.elapsedTime;
        mesh.current.rotation.y += 0.02 * floatSpeed;
        mesh.current.position.y = 0.5 + Math.sin(time * floatSpeed) * 0.2;
    });

    return (
        <group position={data.position}>
            <mesh ref={mesh}>
                {data.type === 'hp' ? ( <dodecahedronGeometry args={[0.4, 0]} /> ) : ( <octahedronGeometry args={[0.4, 0]} /> )}
                <meshStandardMaterial color={color} emissive={color} emissiveIntensity={2} roughness={0.2} metalness={0.8} />
            </mesh>
            <mesh rotation={[-Math.PI/2, 0, 0]} position={[0, 0.05, 0]}>
                <ringGeometry args={[0.4, 0.6, 16]} />
                <meshBasicMaterial color={color} transparent opacity={0.3} side={THREE.DoubleSide} />
            </mesh>
        </group>
    );
};

// 10. Banshee Shield
export const BansheeShield = ({ visible, positionRef }) => {
    const shieldRef = useRef();
    useFrame((state) => {
        if (visible && positionRef.current && shieldRef.current) {
            shieldRef.current.position.set(positionRef.current.x, positionRef.current.y + 0.5, positionRef.current.z);
            const scale = 1 + Math.sin(state.clock.elapsedTime * 3) * 0.05;
            shieldRef.current.scale.set(scale, scale, scale);
            shieldRef.current.rotation.y += 0.01;
        }
    });
    if (!visible) return null;
    return (
        <mesh ref={shieldRef}>
            <sphereGeometry args={[1.2, 32, 32]} />
            <meshStandardMaterial color="#a020f0" transparent opacity={0.4} emissive="#a020f0" emissiveIntensity={0.6} roughness={0.1} metalness={0.9} />
        </mesh>
    );
};

// 11. Ghost Effect
export const GhostEffect = ({ visible, positionRef }) => {
    const ref = useRef();
    useFrame((state) => {
        if (visible && positionRef.current && ref.current) {
            ref.current.position.copy(positionRef.current);
        }
    });
    if (!visible) return null;
    return (
        <mesh ref={ref} position={[0, 0.1, 0]} rotation={[-Math.PI/2, 0, 0]}>
             <ringGeometry args={[0.6, 1.2, 16]} />
             <meshBasicMaterial color="#00ffff" transparent opacity={0.4} />
        </mesh>
    );
};

// 12. KARTHUS ULT EFFECT
export const KarthusUltEffect = ({ playerPosRef, isPaused }) => {
    const cylinderRef = useRef();
    useFrame((state) => {
        if (isPaused || !playerPosRef.current || !cylinderRef.current) return;
        cylinderRef.current.position.set(playerPosRef.current.x, 20, playerPosRef.current.z);
        const time = state.clock.elapsedTime;
        const scale = 1 + Math.sin(time * 10) * 0.2;
        cylinderRef.current.scale.set(scale, 1, scale);
        cylinderRef.current.rotation.y += 0.1;
    });

    return (
        <mesh ref={cylinderRef} position={[0, 20, 0]}>
            <cylinderGeometry args={[1, 1, 40, 32]} />
            <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={2} transparent opacity={0.3} blending={THREE.AdditiveBlending} side={THREE.DoubleSide} depthWrite={false} />
             <mesh position={[0, -20, 0]} rotation={[-Math.PI/2, 0, 0]}>
                 <ringGeometry args={[1, 2, 32]} />
                 <meshBasicMaterial color="#00ff00" transparent opacity={0.5} />
             </mesh>
        </mesh>
    );
};

// 13. JHIN ULT INDICATOR (Fixed)
export const JhinUltIndicator = ({ visible, position, target, duration = 1500 }) => {
    const groupRef = useRef();
    const startTimeRef = useRef(0);
    
    // Geometry'yi hafızada tutuyoruz
    const fillGeometry = useMemo(() => {
        const geo = new THREE.CylinderGeometry(0, 1, 1, 32, 1, true);
        geo.rotateX(-Math.PI / 2);
        geo.translate(0, 0, 0.5); 
        return geo;
    }, []);

    useEffect(() => {
        if (visible) startTimeRef.current = Date.now();
    }, [visible]);

    useFrame((state) => {
        if (!visible || !groupRef.current || !target || !position) return;

        groupRef.current.position.copy(position);
        groupRef.current.lookAt(target.x, 0.2, target.z);

        const elapsed = Date.now() - startTimeRef.current;
        const progress = Math.min(1, elapsed / duration);
        const ease = 1 - Math.pow(1 - progress, 4); 
        
        const range = GAME_CONFIG.JHIN_ULT_RANGE;
        const width = range * 0.4 * ease; 
        
        groupRef.current.scale.set(width, 1, range);
    });

    if (!visible) return null;

    return (
        <group ref={groupRef} frustumCulled={false}>
            <mesh geometry={fillGeometry}>
                <meshBasicMaterial color="#880000" transparent opacity={0.3} side={THREE.DoubleSide} depthWrite={false} />
            </mesh>
        </group>
    );
};